import {
  ProductFeatureV2,
  Available1,
  RoundRobinAvailableAttendees,
  ReservationUsers,
} from '@chilipiper/api-client/src/user-availability'
import { setExtras } from '@chilipiper/sentry'
import invariant from 'tiny-invariant'
import { BookerInvitationSettings, MeetingLimitSource } from '../../load-availability-options'
import { LinkResourceEnhanced, OwnershipLinkRoutingResult } from '../../load-meeting-attempt'
import { isStrictRoundRobinAssignees } from '../../load-slots/types'
import { getOwnershipReservationUsers } from './ownership-users'

export const getRequiredAttendeesForReservation = ({
  linkResource,
  productFeature,
  bookerInvitationSettings,
  ownershipRoutingResult,
  availableRoundRobinAssignees,
  meetingLimit,
  userIds,
}: {
  linkResource?: LinkResourceEnhanced
  productFeature?: ProductFeatureV2
  meetingAttempt?: Available1
  bookerInvitationSettings?: BookerInvitationSettings
  ownershipRoutingResult?: OwnershipLinkRoutingResult
  availableRoundRobinAssignees?: RoundRobinAvailableAttendees[]
  meetingLimit?: MeetingLimitSource
  userIds?: string[]
}): ReservationUsers => {
  const additionalRequiredAttendees: string[] = []

  if (
    bookerInvitationSettings &&
    bookerInvitationSettings.type === 'Invite' &&
    !bookerInvitationSettings.isOptional
  ) {
    additionalRequiredAttendees.push(bookerInvitationSettings.id)
  }

  if (
    productFeature?.type === 'AdminIndividualSchedulingLink' ||
    productFeature?.type === 'PersonalIndividualSchedulingLink'
  ) {
    return {
      type: 'UsersWithLimit',
      userIds: [productFeature.hostId, ...additionalRequiredAttendees],
      limit: meetingLimit ?? { type: 'NoMeetingLimit' },
    }
  }

  if (
    productFeature?.type === 'GroupSchedulingLink' &&
    linkResource?.type === 'GroupSchedulingLink'
  ) {
    return {
      type: 'UsersWithLimit',
      userIds: [linkResource.hostId, ...additionalRequiredAttendees],
      limit: meetingLimit ?? { type: 'NoMeetingLimit' },
    }
  }

  const additionalAttendees =
    additionalRequiredAttendees.length > 0 ? additionalRequiredAttendees : undefined

  if (
    productFeature?.type === 'RoundRobinSchedulingLink' &&
    availableRoundRobinAssignees &&
    isStrictRoundRobinAssignees(availableRoundRobinAssignees)
  ) {
    const attendeesFromDistribution = availableRoundRobinAssignees.map(assignee => ({
      userId: assignee.userId,
      distributionId: assignee.distributionId,
    }))

    return {
      type: 'DistributionUsers',
      attendeesFromDistribution,
      additionalAttendees,
    }
  }

  if (
    productFeature?.type === 'OwnershipSchedulingLink' &&
    ownershipRoutingResult &&
    (availableRoundRobinAssignees || userIds)
  ) {
    const users = getOwnershipReservationUsers({
      availableUsers: availableRoundRobinAssignees,
      ownershipResult: ownershipRoutingResult,
      additionalAttendees,
      userIds,
    })

    invariant(users, 'Users not found')

    return users
  }

  if (productFeature?.type === 'HandoffRouting' && linkResource?.type === 'HandoffRouter') {
    const selectedAssignees = linkResource.selectedAssignees

    if (!selectedAssignees) {
      throw new Error('Selected assignees not found for HandoffRouting')
    }

    const distributionAssignments = selectedAssignees.filter(
      assignee => assignee.type === 'FromDistribution'
    )

    const hasFlexibleDistributionAssignments = distributionAssignments?.some(
      assignment => assignment.algorithm === 'AvailabilityOptimised' && assignment.users.length > 1
    )

    const distributionUserIdAssignments = !hasFlexibleDistributionAssignments
      ? distributionAssignments
          ?.map(assignment => {
            return assignment.users.map(userId => ({
              userId,
              distributionId: assignment.distributionId,
            }))
          })
          .flat()
      : []

    const userFromDistributionAssignments = selectedAssignees
      .filter(assignee => assignee.type === 'UserFromDistribution')
      .map(assignment => ({
        userId: assignment.user,
        distributionId: assignment.distributionId,
      }))

    const userOrOwnershipAssignments = selectedAssignees
      .filter(assignee => assignee.type === 'User')
      .map(assignment => assignment.id)

    if (distributionUserIdAssignments?.length || userFromDistributionAssignments?.length) {
      const additionalAttendees = [...additionalRequiredAttendees, ...userOrOwnershipAssignments]
      return {
        type: 'DistributionUsers',
        additionalAttendees: additionalAttendees.length > 0 ? additionalAttendees : undefined,
        attendeesFromDistribution: [
          ...(distributionUserIdAssignments ?? []),
          ...userFromDistributionAssignments,
        ],
      }
    }

    return {
      type: 'UsersWithLimit',
      limit: meetingLimit ?? {
        type: 'NoMeetingLimit',
      },
      userIds: userOrOwnershipAssignments,
    }
  }

  setExtras({
    linkResource,
    productFeature,
    bookerInvitationSettings,
    ownershipRoutingResult,
    availableRoundRobinAssignees,
    meetingLimit,
    userIds,
  })

  throw new Error('Users not found')
}
